import { AiChat } from '../EnumTypes/ActionEnumTypes';
import * as AiChatConnector from 'Shared/connectors/AiChatConnector';

export const setConnected = (connected: boolean) => ({
  type: AiChat.SET_CONNECTED,
  payload: connected,
});

export const setChats = (chats: any[]) => ({
  type: AiChat.SET_CHATS,
  payload: chats,
});

export const addChat = (chat: any) => ({
  type: AiChat.ADD_CHAT,
  payload: chat,
});

export const removeChat = (chatUuid: string) => ({
  type: AiChat.REMOVE_CHAT,
  payload: chatUuid,
});

export const setActiveChat = (chatUuid: string | null) => ({
  type: AiChat.SET_ACTIVE_CHAT,
  payload: chatUuid,
});

export const setMessages = (messages: any[]) => ({
  type: AiChat.SET_MESSAGES,
  payload: messages,
});

export const addMessage = (message: any) => ({
  type: AiChat.ADD_MESSAGE,
  payload: message,
});

export const setStreaming = (streaming: boolean) => ({
  type: AiChat.SET_STREAMING,
  payload: streaming,
});

export const appendStreamChunk = (chunk: string) => ({
  type: AiChat.APPEND_STREAM_CHUNK,
  payload: chunk,
});

export const finalizeStream = (message: any) => ({
  type: AiChat.FINALIZE_STREAM,
  payload: message,
});

export const setError = (error: string) => ({
  type: AiChat.SET_ERROR,
  payload: error,
});

export const clearError = () => ({
  type: AiChat.CLEAR_ERROR,
});

export const updateChatTitle = (chatUuid: string, title: string) => ({
  type: AiChat.UPDATE_CHAT_TITLE,
  payload: { chatUuid, title },
});

// --- Thunks ---

/**
 * Connect to WebSocket and set up event listeners
 */
export const connectWebSocket = () => (dispatch, getState) => {
  AiChatConnector.connect(
    () => dispatch(setConnected(true)),
    () => dispatch(setConnected(false)),
  );

  // Listen for streamed AI chunks
  AiChatConnector.onStream(({ chatUuid, chunk }) => {
    const { aiChatStore } = getState();
    if (aiChatStore.activeChatUuid === chatUuid) {
      dispatch(appendStreamChunk(chunk));
    }
  });

  // Listen for stream completion
  AiChatConnector.onStreamEnd(({ chatUuid, message }) => {
    const { aiChatStore } = getState();
    if (aiChatStore.activeChatUuid === chatUuid) {
      dispatch(finalizeStream(message));
    }
  });

  // Listen for stream errors
  AiChatConnector.onStreamError(({ chatUuid, error }) => {
    const { aiChatStore } = getState();
    if (aiChatStore.activeChatUuid === chatUuid) {
      dispatch(setStreaming(false));
      dispatch(setError(error));
    }
  });

  // Listen for saved user messages
  AiChatConnector.onMessageSaved(({ chatUuid, message }) => {
    const { aiChatStore } = getState();
    if (aiChatStore.activeChatUuid === chatUuid) {
      dispatch(addMessage(message));
      dispatch(setStreaming(true));
    }
  });

  // Listen for title updates
  AiChatConnector.onTitleUpdated(({ chatUuid, title }) => {
    dispatch(updateChatTitle(chatUuid, title));
  });
};

/**
 * Disconnect from WebSocket
 */
export const disconnectWebSocket = () => (dispatch) => {
  AiChatConnector.removeAllListeners();
  AiChatConnector.disconnect();
  dispatch(setConnected(false));
};

/**
 * Fetch the chat list
 */
export const fetchChats = () => async (dispatch) => {
  try {
    const chats = await AiChatConnector.listChats();
    dispatch(setChats(chats));
  } catch (err: any) {
    console.error('[AiChat] Failed to fetch chats:', err.message);
  }
};

/**
 * Create a new chat and set it as active
 */
export const createNewChat = () => async (dispatch) => {
  try {
    const chat = await AiChatConnector.createChat();
    dispatch(addChat(chat));
    dispatch(setActiveChat(chat.uuid));
    dispatch(setMessages([]));
    return chat;
  } catch (err: any) {
    console.error('[AiChat] Failed to create chat:', err.message);
    dispatch(setError('Nie udało się utworzyć czatu.'));
  }
};

/**
 * Create a new chat and immediately send a message in it
 */
export const createNewChatAndSend = (content: string) => async (dispatch) => {
  try {
    const chat = await AiChatConnector.createChat();
    dispatch(addChat(chat));
    dispatch(setActiveChat(chat.uuid));
    dispatch(setMessages([]));
    dispatch(clearError());
    AiChatConnector.sendMessage(chat.uuid, content);
  } catch (err: any) {
    console.error('[AiChat] Failed to create chat:', err.message);
    dispatch(setError('Nie udało się utworzyć czatu.'));
  }
};

/**
 * Load history for a specific chat
 */
export const loadChatHistory = (chatUuid: string) => async (dispatch) => {
  try {
    dispatch(setActiveChat(chatUuid));
    const messages = await AiChatConnector.getChatHistory(chatUuid);
    dispatch(setMessages(messages));
  } catch (err: any) {
    console.error('[AiChat] Failed to load history:', err.message);
    dispatch(setError('Nie udało się załadować historii czatu.'));
  }
};

/**
 * Send a message in the active chat
 */
export const sendChatMessage = (chatUuid: string, content: string) => (dispatch) => {
  dispatch(clearError());
  AiChatConnector.sendMessage(chatUuid, content);
};

/**
 * Delete a chat
 */
export const deleteChatAction = (chatUuid: string) => async (dispatch) => {
  try {
    await AiChatConnector.deleteChat(chatUuid);
    dispatch(removeChat(chatUuid));
  } catch (err: any) {
    console.error('[AiChat] Failed to delete chat:', err.message);
  }
};
